import { eq } from "@-/com.npmjs.drizzle-orm";
import type { ActorDatabase } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema, type UserCard } from "@-/dev.improve.haniverse.database.schemas.actor.schema";

/**
 * The `user_card` columns enrolling a card writes: everything {@link enrollGraphemeCardTargets}
 * sets, plus the review recharge a lesson enrollment records on the meaning card.
 */
type EnrollmentColumns = Pick<
  UserCard,
  | "deferredUntil"
  | "deletedAt"
  | "fsrsCardId"
  | "queuedAt"
  | "reviewRechargeAfter"
  | "startedStudyingAt"
>;

type GraphemeCardRow = {
  /** `null` when the link points at a `user_card` row that does not exist. */
  card: EnrollmentColumns | null;
  link: { deletedAt: string | null; id: string; table: "meaning" | "pronunciation" };
};

/** Every meaning and pronunciation card row of one grapheme, tombstones included, by `user_card.id`. */
export type GraphemeEnrollmentState = ReadonlyMap<string, GraphemeCardRow>;

/** One card an enrollment brought into study, and what its rows held before. */
export type GraphemeEnrollmentChange = {
  /** `null` when the enrollment created the card and its grapheme link. */
  before: { card: EnrollmentColumns | null; linkDeletedAt: string | null } | null;
  /** The card's scheduler row, which the enrollment started over. */
  fsrsCardId: string;
  link: { id: string; table: "meaning" | "pronunciation" };
  /**
   * When this enrollment put the card in study. It tells this enrollment apart from a later one:
   * a card keeps one scheduler row for good, so `fsrsCardId` alone cannot.
   */
  startedStudyingAt: string | null;
  userCardId: string;
};

/**
 * Read every card row of `grapheme` that enrollment can touch, tombstones included — the
 * enrollment resurrects soft-deleted meaning rows, so a "before" that hid them could not be
 * restored. Pair a read taken before an enrollment with one taken after, through
 * {@link diffGraphemeEnrollment}.
 */
export async function readGraphemeEnrollmentState(
  actorDb: ActorDatabase,
  grapheme: string,
): Promise<GraphemeEnrollmentState> {
  return actorDb(import.meta.location).transaction(async (tx) => {
    const meaningLinks = await tx.queryRaw.userGraphemeMeaningCard.findMany({
      columns: { deletedAt: true, id: true, userCardId: true },
      where: { grapheme: { eq: grapheme } },
    });
    const pronunciationLinks = await tx.queryRaw.userGraphemePronunciationCard.findMany({
      columns: { deletedAt: true, id: true, userCardId: true },
      where: { grapheme: { eq: grapheme } },
    });
    const links = [
      ...meaningLinks.map((link) => ({ ...link, table: "meaning" as const })),
      ...pronunciationLinks.map((link) => ({ ...link, table: "pronunciation" as const })),
    ];
    // Read directly, not through the link's `userCard` relation: that relation hides
    // soft-deleted cards even on the raw handle.
    const cards = await tx.queryRaw.userCard.findMany({
      columns: {
        deferredUntil: true,
        deletedAt: true,
        fsrsCardId: true,
        id: true,
        queuedAt: true,
        reviewRechargeAfter: true,
        startedStudyingAt: true,
      },
      where: { id: { in: links.map((link) => link.userCardId) } },
    });
    const cardById = new Map(cards.map(({ id, ...columns }) => [id, columns]));
    return new Map(
      links.map((link) => [
        link.userCardId,
        {
          card: cardById.get(link.userCardId) ?? null,
          link: { deletedAt: link.deletedAt, id: link.id, table: link.table },
        },
      ]),
    );
  });
}

function isEnrolled(row: GraphemeCardRow | undefined): row is GraphemeCardRow & {
  card: EnrollmentColumns & { fsrsCardId: string };
} {
  return (
    row !== undefined &&
    row.link.deletedAt === null &&
    row.card !== null &&
    row.card.deletedAt === null &&
    row.card.fsrsCardId !== null
  );
}

/**
 * The cards an enrollment brought into study: in study after it, not before. A card that was
 * already in study is left out — enrollment does not touch it, so undo must not either.
 */
export function diffGraphemeEnrollment(
  before: GraphemeEnrollmentState,
  after: GraphemeEnrollmentState,
): GraphemeEnrollmentChange[] {
  const changes: GraphemeEnrollmentChange[] = [];
  for (const [userCardId, row] of after) {
    const previous = before.get(userCardId);
    if (!isEnrolled(row) || isEnrolled(previous)) {
      continue;
    }
    changes.push({
      before:
        previous === undefined
          ? null
          : { card: previous.card, linkDeletedAt: previous.link.deletedAt },
      fsrsCardId: row.card.fsrsCardId,
      link: { id: row.link.id, table: row.link.table },
      startedStudyingAt: row.card.startedStudyingAt,
      userCardId,
    });
  }
  return changes;
}

/**
 * Take an enrollment back out of study, returning each card it enrolled to exactly the rows it
 * had before: a card the enrollment created is tombstoned with its grapheme link, and a card it
 * revived (a deferred, queued, favorited or reset one) gets its previous columns back. The
 * scheduler row is soft-deleted — these tables sync, and a hard delete would have to race the
 * change feed.
 *
 * Idempotent, and never clobbers a later change: a card that is no longer in study through this
 * enrollment (already undone, reset, re-enrolled) is left alone.
 */
export async function undoGraphemeEnrollment({
  actorDb,
  changes,
  now,
}: {
  actorDb: ActorDatabase;
  changes: readonly GraphemeEnrollmentChange[];
  now: Date;
}): Promise<void> {
  if (changes.length === 0) {
    return;
  }
  const nowIso = now.toISOString();
  await actorDb(import.meta.location).transaction(async (tx) => {
    for (const change of changes) {
      const card = await tx.queryRaw.userCard.findFirst({
        columns: { deletedAt: true, fsrsCardId: true, startedStudyingAt: true },
        where: { id: { eq: change.userCardId } },
      });
      if (
        card?.fsrsCardId !== change.fsrsCardId ||
        card.startedStudyingAt !== change.startedStudyingAt ||
        card.deletedAt !== null
      ) {
        continue;
      }
      await tx
        .update(actorSchema.fsrsCard)
        .set({ deletedAt: nowIso })
        .where(eq(actorSchema.fsrsCard.id, change.fsrsCardId))
        .run();
      await tx
        .update(actorSchema.userCard)
        .set(change.before?.card ?? { deletedAt: nowIso })
        .where(eq(actorSchema.userCard.id, change.userCardId))
        .run();
      const linkDeletedAt = change.before === null ? nowIso : change.before.linkDeletedAt;
      await (change.link.table === "meaning"
        ? tx
            .update(actorSchema.userGraphemeMeaningCard)
            .set({ deletedAt: linkDeletedAt })
            .where(eq(actorSchema.userGraphemeMeaningCard.id, change.link.id))
            .run()
        : tx
            .update(actorSchema.userGraphemePronunciationCard)
            .set({ deletedAt: linkDeletedAt })
            .where(eq(actorSchema.userGraphemePronunciationCard.id, change.link.id))
            .run());
    }
  });
}
